import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Loader2, Heart, MessageCircle, Eye, Send, Users } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";

const RANGES = [["day", "يوم", "Day"], ["week", "أسبوع", "Week"], ["month", "شهر", "Month"], ["year", "سنة", "Year"]];
const PREV = { day: "أمس", week: "الأسبوع الماضي", month: "الشهر الماضي", year: "السنة الماضية" };
const PREV_EN = { day: "yesterday", week: "last week", month: "last month", year: "last year" };

const Delta = ({ icon: Icon, label, d, testid }) => {
  const { t } = useLang();
  const up = d.pct >= 0;
  return (
    <div className="border border-border rounded-xl px-4 py-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-xl font-extrabold mt-1">{Number(d.now).toLocaleString("en-US")}</div>
      <div className={`text-[11px] flex items-center gap-1 mt-0.5 ${up ? "text-emerald-600" : "text-rose-600"}`}>
        {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {up ? "+" : ""}{d.pct}% {t("مقارنة بـ", "vs")}{""}
        <span className="text-muted-foreground">({Number(d.prev).toLocaleString("en-US")})</span>
      </div>
    </div>
  );
};

export const GrowthCharts = ({ projectId, onRange }) => {
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const { t, lang } = useLang();
  useEffect(() => {
    setData(null); onRange?.(range);
    api.get(`/social/growth?range=${range}${projectId ? `&project_id=${projectId}` : ""}`).then((r) => setData(r.data)).catch(() => setData({ series: [], delta: null }));
  }, [range, projectId]); // eslint-disable-line

  return (
    <div className="glass rounded-2xl p-5 border border-border" data-testid="growth-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /><h2 className="font-bold">{t("نمو حساباتك", "Account growth")}</h2></div>
        <div className="flex rounded-xl bg-secondary p-1 gap-1" data-testid="growth-range-tabs">
          {RANGES.map(([k, l, en]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`growth-range-${k}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${range === k ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>{t(l, en)}</button>
          ))}
        </div>
      </div>
      {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
        <>
          {data.delta && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
              <Delta icon={Send} label={t("التفاعل الكلي", "Total interactions")} d={data.delta.interactions} testid="delta-interactions" />
              <Delta icon={Heart} label={t("الإعجابات", "Likes")} d={data.delta.likes} testid="delta-likes" />
              <Delta icon={MessageCircle} label={t("التعليقات", "Comments")} d={data.delta.comments} testid="delta-comments" />
              <Delta icon={Eye} label={t("المشاهدات", "Views")} d={data.delta.views} testid="delta-views" />
              <div className="border border-border rounded-xl px-4 py-3" data-testid="followers-box">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="w-3.5 h-3.5" /> {t("المتابعون", "Followers")}</div>
                <div className="text-xl font-extrabold mt-1">{Number(data.followers || 0).toLocaleString("en-US")}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{t("المقارنة مع", "Compared with")} {lang === "en" ? PREV_EN[range] : PREV[range]}</div>
              </div>
            </div>
          )}
          {!data.has_data && <p className="text-xs text-accent bg-accent/10 border border-accent/30 rounded-lg px-3 py-2 mb-4" data-testid="growth-empty">{t("لا توجد أرقام بعد — تظهر البيانات تلقائياً بعد ربط حساباتك ونشر أول منشور، أو من أرقام الأداء التي تُدخلها في الحملات.", "No numbers yet — data appears automatically after you connect your accounts and publish your first post, or from the performance numbers you enter in campaigns.")}</p>}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="h-56">
              <div className="text-xs text-muted-foreground mb-1">{t("التفاعل والمشاهدات", "Interactions & views")}</div>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series}>
                  <defs>
                    <linearGradient id="gI" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F766E" stopOpacity={0.5} /><stop offset="100%" stopColor="#0F766E" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D89134" stopOpacity={0.5} /><stop offset="100%" stopColor="#D89134" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.15)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={36} />
                  <Tooltip contentStyle={{ fontSize: 12, direction: lang === "en" ? "ltr" : "rtl" }} />
                  <Area type="monotone" dataKey="interactions" name={t("التفاعل", "Interactions")} stroke="#0F766E" fill="url(#gI)" strokeWidth={2} />
                  <Area type="monotone" dataKey="views" name={t("المشاهدات", "Views")} stroke="#D89134" fill="url(#gV)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="h-56">
              <div className="text-xs text-muted-foreground mb-1">{t("المنشورات والمتابعون", "Posts & followers")}</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.15)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={36} />
                  <Tooltip contentStyle={{ fontSize: 12, direction: lang === "en" ? "ltr" : "rtl" }} />
                  <Bar dataKey="posts" name={t("المنشورات", "Posts")} fill="#734F96" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="followers" name={t("المتابعون", "Followers")} fill="#1D4ED8" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
